// https://gist.github.com/oskansavli/822382
// noinspection ES6ConvertVarToLetConst,JSAssignmentUsedAsCondition,JSPrimitiveTypeWrapperUsage,JSDuplicatedDeclaration

interface DecimalFormatConfig {
  prefix: string
  suffix: string
  comma: number
  minInt: number
  minFrac: number
  maxFrac: number
}

const createDecimalFormat = (formatStr: string): DecimalFormatConfig => {
  let prefix = ''
  let suffix = ''
  let comma = 0
  let minInt = 1
  let minFrac = 0
  let maxFrac = 0

  // get prefix
  for (let i = 0; i < formatStr.length; i++) {
    if (formatStr.charAt(i) === '#' || formatStr.charAt(i) === '0') {
      prefix = formatStr.substring(0, i)
      formatStr = formatStr.substring(i)
      break
    }
  }

  // get suffix
  suffix = formatStr.replace(/[#]|[0]|[,]|[.]/g, '')

  // get number as string
  const numberStr = formatStr.replace(/[^0#,.]/g, '')

  let intStr = ''
  let fracStr = ''
  const point = numberStr.indexOf('.')
  if (point !== -1) {
    intStr = numberStr.substring(0, point)
    fracStr = numberStr.substring(point + 1)
  } else {
    intStr = numberStr
  }

  const commaPos = intStr.lastIndexOf(',')
  if (commaPos !== -1) {
    comma = intStr.length - 1 - commaPos
  }

  intStr = intStr.replace(/[,]/g, '') // remove commas
  fracStr = fracStr.replace(/[,]|[.]+/g, '')

  maxFrac = fracStr.length
  let tmp = intStr.replace(/[^0]/g, '') // remove all except zero
  if (tmp.length > minInt) {
    minInt = tmp.length
  }
  tmp = fracStr.replace(/[^0]/g, '')
  minFrac = tmp.length

  return {
    prefix,
    suffix,
    comma,
    minInt,
    minFrac,
    maxFrac,
  }
}

const getNumericString = (str: string): string => {
  const num = Number(str)
  const numStr = num.toString()
  if (str.indexOf('.') > -1 && numStr.indexOf('.') < 0) {
    for (let i = str.indexOf('.') + 1; i < str.length; i++) {
      if (str.charAt(i) !== '0') return str
    }
    return numStr
  }
  return str
}

const formatBack = (
  config: DecimalFormatConfig,
  fNumStr: string | number
): string => {
  const str = fNumStr.toString()
  if (!str) return ''
  if (!isNaN(Number(str))) return getNumericString(str)

  let fNumberStr = str
  let negative = false

  if (str.charAt(0) === '-') {
    fNumberStr = fNumberStr.substr(1)
    negative = true
  }

  const pIndex = fNumberStr.indexOf(config.prefix)
  const sIndex =
    config.suffix === ''
      ? fNumberStr.length
      : fNumberStr.indexOf(config.suffix, config.prefix.length + 1)

  if (pIndex === 0 && sIndex > 0) {
    fNumberStr = fNumberStr.substr(0, sIndex)
    fNumberStr = fNumberStr.substr(config.prefix.length)
    fNumberStr = fNumberStr.replace(/,/g, '')

    if (negative) {
      fNumberStr = '-' + fNumberStr
    }

    if (!isNaN(Number(fNumberStr))) {
      return getNumericString(fNumberStr)
    }
  }

  return str
}

const format = (
  config: DecimalFormatConfig,
  numStr: string | number
): string => {
  let numberStr = formatBack(config, numStr).toLowerCase()

  if (isNaN(Number(numberStr)) || numberStr.length === 0) {
    return numStr.toString()
  }

  // Handle scientific numbers
  if (numberStr.indexOf('e') !== -1) {
    const n = Number(numberStr)
    if (n === Infinity || n === -Infinity) return numberStr.toString()
    numberStr = n.toString()
    if (numberStr.indexOf('e') !== -1) return numberStr
  }

  let negative = false
  if (numberStr.charAt(0) === '-') {
    negative = true
    numberStr = numberStr.substring(1)
  } else if (numberStr.charAt(0) === '+') {
    numberStr = numberStr.substring(1)
  }

  const point = numberStr.indexOf('.')
  let intStr = ''
  let fracStr = ''

  if (point !== -1) {
    intStr = numberStr.substring(0, point)
    fracStr = numberStr.substring(point + 1)
  } else {
    intStr = numberStr
  }

  fracStr = fracStr.replace(/[.]/, '')

  const isPercentage = config.suffix && config.suffix.charAt(0) === '%'

  if (isPercentage) {
    intStr = intStr + ('00' + (fracStr + '00').substr(0, 2)).substr(-2)
    fracStr = fracStr.substr(2)
  }

  if (fracStr.length > config.maxFrac) {
    const num = Number('0.' + fracStr)
    const roundedNum =
      config.maxFrac === 0
        ? Math.round(num)
        : Number(num.toFixed(config.maxFrac))
    fracStr = roundedNum.toString(10).substr(2)

    let carry = roundedNum >= 1 ? 1 : 0
    let i = intStr.length - 1

    while (carry) {
      if (i === -1) {
        intStr = '1' + intStr
        break
      } else {
        let x = intStr.charAt(i)
        if (x === '9') {
          x = '0'
          carry = 1
        } else {
          x = String(Number(x) + 1)
          carry = 0
        }
        intStr = intStr.substring(0, i) + x + intStr.substring(i + 1)
        i--
      }
    }
  }

  for (let i = fracStr.length; i < config.minFrac; i++) {
    fracStr = fracStr + '0'
  }

  while (
    fracStr.length > config.minFrac &&
    fracStr.charAt(fracStr.length - 1) === '0'
  ) {
    fracStr = fracStr.substring(0, fracStr.length - 1)
  }

  for (let i = intStr.length; i < config.minInt; i++) {
    intStr = '0' + intStr
  }

  while (intStr.length > config.minInt && intStr.charAt(0) === '0') {
    intStr = intStr.substring(1)
  }

  let j = 0
  for (let i = intStr.length; i > 0; i--) {
    if (j !== 0 && j % config.comma === 0) {
      intStr = intStr.substring(0, i) + ',' + intStr.substring(i)
      j = 0
    }
    j++
  }

  let formattedValue = config.prefix + intStr
  if (fracStr.length > 0) {
    formattedValue += '.' + fracStr
  }
  formattedValue += config.suffix

  if (negative) {
    formattedValue = '-' + formattedValue
  }

  return formattedValue
}

export const createDecimalFormatter = (formatStr: string) => {
  const config = createDecimalFormat(formatStr)
  return {
    format: (num: string | number) => format(config, num),
    formatBack: (num: string | number) => formatBack(config, num),
  }
}

export default createDecimalFormatter
